import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from "react-toastify";
import { useTokenInfo } from '../../../../hooks/useTokenInfo';
import { useWithdrawRewards } from '../hooks/useWithdrawRewards';
import Loader from '../../../../shared/Loader';
import TokenToDollor from '../../../../shared/TokenToDollor';
import { useTheme } from '../../../../hooks/ThemeProvider';
import { EXPLORER_URL } from '../../../../utils/constant';

function WithdrawRewardsModals({ show, onHide, poolData, fetchRootPools }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [gasEstimate, setGasEstimate] = useState(null);
    const [transactionHash, setTransactionHash] = useState('');
 const { theme } = useTheme();
    const {
        balance,
        balanceWei,
        contractBalance,  // This is the unused rewards balance
        symbol,
        decimals,
        isLoading: isLoadingToken
    } = useTokenInfo(poolData?.rewardToken, null, poolData?.contractAddress);

    const { withdrawRewards, estimateWithdrawGas, isWithdrawInProgress, isEstimatingGas } = useWithdrawRewards();

    // console.log("Pool Data in WithdrawRewardsModals:", poolData);

    const handleClose = () => {
        setCurrentStep(1);
        setGasEstimate(null);
        setTransactionHash('');
        onHide();
    };

    const handleContinue = async () => {
        if (!contractBalance || contractBalance <= 0) {
            toast.error("No unused rewards available to withdraw");
            return;
        }

        try {
            // Estimate gas
            const estimate = await estimateWithdrawGas(poolData?.contractAddress);
            setGasEstimate(estimate);
            setCurrentStep(2);
        } catch (error) {
            console.error("Gas estimation error:", error);
            toast.error("Failed to estimate gas fees. Please try again.");
        }
    };

    const handleConfirm = async () => {
        try {
            // Execute withdrawal
            const result = await withdrawRewards(poolData?.contractAddress);
            fetchRootPools();
            setTransactionHash(result.hash);
            setCurrentStep(3);
        } catch (error) {
            console.error("Withdrawal error:", error);
            if (error.message?.includes("User rejected")) {
                toast.error("Transaction rejected by user");
            } else {
                toast.error("Failed to withdraw rewards. Please try again.");
            }
        }
    };

    const handleFinalClose = () => {
        handleClose();
    };

    const truncateHash = (hash) => {
        if (!hash) return "0x74rhiw4rf38u...5r2";
        return `${hash.slice(0, 10)}...${hash.slice(-3)}`;
    };

    const isProcessing = isWithdrawInProgress || isEstimatingGas;
    const getLoadingText = () => {
        if (isWithdrawInProgress) return 'Processing Withdrawal...';
        if (isEstimatingGas) return 'Estimating Gas Fees...';
        if (isLoadingToken) return 'Loading Token Info...';
        return 'Loading...';
    };

    return (
        <>
            {isProcessing && <Loader loadingText={getLoadingText()} />}

            {/* Step 1: Withdraw Confirmation Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 1 && !isProcessing}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Withdraw Unused Rewards</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="modal_share">
                        <div className="inner_type">
                            <div className="one_modal">
                                <p>Unused Rewards</p>
                                <h3>
                                    {isLoadingToken
                                        ? "Loading..."
                                        : `${contractBalance ? contractBalance.toLocaleString() : '0'} ${symbol || 'OASIS'}`
                                    }
                                </h3>
                                <p>~<TokenToDollor tokenAddress={poolData?.rewardToken} tokenAmount={contractBalance || '0'} /></p>
                            </div>
                        </div>
                        <div className="buton_modal_div">
                            <button
                                onClick={handleContinue}
                                disabled={isProcessing || !contractBalance || contractBalance <= 0}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Step 2: Finalize Withdrawal Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 2 && !isProcessing}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Finalize Withdrawal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="modal_share">
                        <div className="inner_type">
                            <div className="one_modal">
                                <p>Withdrawal Amount</p>
                                <h3>
                                    {contractBalance ? contractBalance.toLocaleString() : '0'} {symbol || 'OASIS'}
                                </h3>
                                {/* <p className="numbers">~$0.00</p> */}
                            </div>
                            <div className="main_lock">
                                <div className="inner_lock">
                                    <p>Gas Fees</p>
                                    <span>
                                        <TokenToDollor tokenSymbol="" tokenAmount={gasEstimate?.totalGasCostETHFormatted || '0'} />
                                        ({gasEstimate?.totalGasCostETHFormatted || '0'} ETH)
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="buton_modal_div">
                            <button
                                onClick={handleConfirm}
                                disabled={isProcessing}
                            >
                                {isWithdrawInProgress ? "Processing..." : "Confirm"}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Step 3: Success Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 3 && !isProcessing}
                onHide={handleFinalClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="succes_modal">
                        <div className="top_success">
                            <img src="/assets/tick.svg" alt="success" />
                            <h3>Withdrawal Successful</h3>
                            <p>
                                You've successfully withdrawn the unused rewards back to your
                                wallet.
                            </p>
                        </div>
                        <div className="hash_div">
                            <div>
                                <p>Transaction Hash</p>
                                <span>{truncateHash(transactionHash)}</span>
                            </div>
                            <button
                                onClick={() => window.open(`${EXPLORER_URL}${transactionHash}`, '_blank')}
                            >
                                  {theme === 'dark' ? (
                            <img src="/assets/whitetab.svg" alt="view" />
                    ) : (
                     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
  <path d="M12.25 8.08333V11.5833C12.25 11.8928 12.1271 12.1895 11.9083 12.4083C11.6895 12.6271 11.3928 12.75 11.0833 12.75H2.91667C2.60725 12.75 2.3105 12.6271 2.09171 12.4083C1.87292 12.1895 1.75 11.8928 1.75 11.5833V3.41667C1.75 3.10725 1.87292 2.8105 2.09171 2.59171C2.3105 2.37292 2.60725 2.25 2.91667 2.25H6.41667" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.25 2.25L7 7.5" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.75 2.25H12.25V5.75" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                    )}
                                View
                            </button>
                        </div>
                        <div className="buton_modal_div">
                            <button onClick={handleFinalClose}>Close</button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default WithdrawRewardsModals;